import { compare } from '@shared/helpers/compare.helpers';
import { replaceProperties } from '@shared/helpers/object.helpers';
import { DraftShape, Shape } from '@shared/models/design/storey.model';
import { Point2D } from '@shared/models/geometry.model';
import { designGuards } from '../design-typeguards.helpers';
import {
  isEqualPoints,
  isPolygon2D,
} from '@shared/helpers/geometry/geometry.helpers';
import { guards } from '@shared/helpers/typeguards.helpers';
import { insertItems } from '@shared/helpers/array.helpers';

/**
 * Update a shape or draft shape
 * @param shape
 * @param update
 */
function updateShape(
  shape: DraftShape,
  update: Partial<DraftShape>,
): DraftShape;
function updateShape(shape: Shape, update: Partial<Shape>): Shape;
function updateShape(
  shape: Shape | DraftShape,
  update: Partial<Shape> | Partial<DraftShape>,
) {
  return replaceProperties(shape, update);
}

// Update points on a shape or draft shape
const setShapePoints = <T extends Shape | DraftShape>(
  shape: T,
  points: Point2D[],
): T => {
  // Don't update if the points are the same
  if (compare.equal.deep(shape.points, points)) return shape;

  if (designGuards.is.draftShape(shape)) {
    return updateShape(shape, { points }) as T;
  }

  if (isPolygon2D(points)) {
    // Return the shape with the new points
    return updateShape(shape, { points }) as T;
  }

  // TODO Error handling
  throw new Error('Points must be an array with at least 3 points');
};

/**
 * Add one or more points to a shape or draft shape
 * @param shape
 * @param points
 * @returns
 */
const addShapePoints = <T extends Shape | DraftShape>(
  shape: T,
  ...points: Point2D[]
): T => setShapePoints(shape, [...shape.points, ...points]);

/**
 * Remove a point from a shape or draft shape
 * @param shape
 * @param indexOrPoint
 * @returns
 */
const removeShapePoints = <T extends Shape | DraftShape>(
  shape: T,
  ...indexesOrPoints: (number | Point2D)[]
): T => {
  const points = shape.points;
  // How many points can be removed
  const allowedRemovalCount = designGuards.is.draftShape(shape)
    ? points.length
    : Math.max(points.length - 3, 0);
  const indexesToRemove = indexesOrPoints
    .map((indexOrPoint) =>
      typeof indexOrPoint === 'number'
        ? indexOrPoint
        : points.findIndex((p) => isEqualPoints(p, indexOrPoint)),
    )
    .filter((index) => guards.is.numberBetween(index, 0, points.length - 1)) // Remove out of range indexes
    .slice(0, allowedRemovalCount); // Make sure we don't remove more than allowed

  return setShapePoints(
    shape,
    points.filter((_, i) => !indexesToRemove.includes(i)),
  );
};

/**
 * Move a point in a shape or draft shape
 * @param shape
 * @param existingIndexOrPoint
 * @param newPosition
 * @returns
 */
const moveShapePoint = <T extends Shape | DraftShape>(
  shape: T,
  existingIndexOrPoint: number | Point2D,
  newPosition: Point2D,
): T => {
  const points = shape.points;
  const index =
    typeof existingIndexOrPoint === 'number'
      ? existingIndexOrPoint
      : points.findIndex((p) => compare.equal.deep(p, existingIndexOrPoint));
  return setShapePoints(
    shape,
    points.map((p, i) => (i === index ? newPosition : p)),
  );
};

const insertShapePointAt = <T extends Shape | DraftShape>(
  shape: T,
  index: number,
  ...points: Point2D[]
): T => {
  return setShapePoints(shape, insertItems(shape.points, index, points));
};

/**
 * Translate multiple points in a shape by a delta
 * If indexes are provided, only translate the points at the given indexes
 * If indexes are not provided, translate all points
 * @param shape
 * @param delta
 * @param indexes? - (optional) The indexes of the points to translate. If not provided, all points will be translated.
 * @returns The translated shape
 * @example
 * const shape = { points: [[0, 0], [10, 10], [20, 20]] };
 * const delta = [10, 10];
 * const indexes = [0, 2];
 * const translatedShape = translateShapePoints(shape, delta, indexes);
 * // translatedShape.points = [[10, 10], [10, 10], [30, 30]]
 */
const translateShapePoints = <T extends Shape | DraftShape>(
  shape: T,
  delta: Point2D,
  indexes?: number[],
): T => {
  const [dx, dy] = guards.is.array(delta) ? delta : [0, 0];
  if (!dx && !dy) return shape;

  const next = shape.points.map((p, i) =>
    guards.is.undefined(indexes) || indexes.includes(i)
      ? [p[0] + dx, p[1] + dy]
      : p,
  ) as Point2D[];
  return setShapePoints(shape, next);
};

// Todo: Add unit tests
export const mutateShape = {
  update: updateShape,
  point: {
    set: setShapePoints,
    add: addShapePoints,
    insertAt: insertShapePointAt,
    remove: removeShapePoints,
    move: moveShapePoint,
    translateMany: translateShapePoints,
  },
} as const;
